import { Box, Chip, SxProps, Theme, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { getClientJourneyFlowType, getGoalPriorityEnum, getPriorityLabel, priorityOptionsFlow2 } from '../../../utils';

interface GoalPriorityChipProps {
    goalPriority?: string;
    className?: string;
    dataTestId?: string;
}

type PriorityOption = { index: number; label: string; value: string };

// Figma's flow2 pill is 61px wide; longer labels (e.g. Spanish) widen it instead of being cut off.
const FLOW2_MIN_WIDTH = '61px';

const getPillBaseSx = (theme: Theme) => ({
    backgroundColor: theme.palette.neutral.white,
    border: `1px solid ${theme.palette.neutral[500]}`,
    borderRadius: '100px',
    fontWeight: 400,
    color: theme.palette.semantic.text.default,
    '& .MuiChip-label': { display: 'flex', alignItems: 'center' }
});

// Pill size and text for flows other than flow2.
const DEFAULT_SX = {
    height: '24px',
    fontSize: '12px',
    lineHeight: '17px',
    '& .MuiChip-label': { padding: '0 12px' }
};

/**
 * Flow2 (Banorte) Figma priority pill: 14/19 text, at least 61px wide, 24px tall, or 22px when `dense`
 * (contribution schedule). The theme pads every chip by 4px, which the Figma pill doesn't have.
 */
export const getFlow2PriorityPillSx = (theme: Theme, dense = false): SxProps<Theme> => [
    getPillBaseSx(theme),
    {
        height: theme.spacing(dense ? 2.75 : 3),
        minWidth: FLOW2_MIN_WIDTH,
        padding: 0,
        fontSize: '14px',
        lineHeight: '19px',
        letterSpacing: 0,
        '& .MuiChip-label': { px: 0.375 }
    }
];

/**
 * Shared goal-priority pill. Flow2 (Banorte) renders the Figma pill with the priority order before the label
 * ("1-Need"); flow1/default tenants show the plain label.
 * All pills render at the width of the widest priority label so they line up regardless of the
 * selected value, with the visible label centred.
 */
export const GoalPriorityChip = ({
    goalPriority,
    className,
    dataTestId
}: GoalPriorityChipProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { goeConfig } = useAppSelector((state) => state.global);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const goalPriorityEnum = getGoalPriorityEnum(goeConfig?.['goalPriorityProbabilities']);
    const options = priorityOptionsFlow2(t, goalPriorityEnum) as PriorityOption[];
    const selected = options.find((option) => option.value === goalPriority);
    if (!selected) return null;

    const formatLabel = (option: PriorityOption) => {
        const label = getPriorityLabel(option, tenant, t);
        return isFlow2 ? `${option.index + 1}-${label}` : label;
    };

    return (
        <Chip
            className={className}
            data-testid={dataTestId}
            size="small"
            variant="outlined"
            label={
                <Box sx={{ display: 'inline-grid', placeItems: 'center' }}>
                    {options.map((option) => (
                        <Box key={option.value} aria-hidden sx={{ gridArea: '1 / 1', visibility: 'hidden' }}>
                            {formatLabel(option)}
                        </Box>
                    ))}
                    <Box sx={{ gridArea: '1 / 1' }}>{formatLabel(selected)}</Box>
                </Box>
            }
            sx={isFlow2 ? getFlow2PriorityPillSx(theme) : [getPillBaseSx(theme), DEFAULT_SX]}
        />
    );
};
